'use client';

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type ClientStory, loadPublishedClientStories } from '@/app/lib/clientStories';
import { SECTION_HEADING_CLASS, SECTION_HEADING_STYLE } from './sectionHeading';

type Testimonial = ClientStory;

export default function TestimonialsSection() {
  // Starts empty and stays empty until Supabase returns published stories.
  // It used to start from the built-in seed, which meant six fabricated
  // testimonials were the first thing painted on every visit. The whole
  // section — layout, carousel, admin panel — is intact and returns the moment
  // a real story is published.
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadPublishedClientStories().then((res) => {
      // An unconfigured or failing backend leaves the list empty, which drops
      // the section rather than showing quotes nobody gave.
      if (!cancelled && res.stories.length) {
        setTestimonials(res.stories);
        setActiveIndex((i) => (i < res.stories.length ? i : 0));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const startAutoPlay = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setDirection(1);
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5500);
  };

  // Re-armed when the count changes: the interval closes over
  // testimonials.length, so a stale one could advance past the end of a
  // shorter managed list.
  useEffect(() => {
    startAutoPlay();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testimonials.length]);

  const prev = () => {
    setDirection(-1);
    setActiveIndex((i) => (i - 1 + testimonials.length) % testimonials.length);
    startAutoPlay();
  };

  const next = () => {
    setDirection(1);
    setActiveIndex((i) => (i + 1) % testimonials.length);
    startAutoPlay();
  };

  const active = testimonials[activeIndex] ?? testimonials[0];

  // Nothing published: drop the section rather than render an empty frame or,
  // worse, invented quotes. Publish a real story in the admin dashboard and
  // this returns exactly as it was.
  if (!active) return null;

  const slideVariants = {
    enter: (dir: number) => ({ opacity: 0, x: dir > 0 ? 40 : -40 }),
    center: { opacity: 1, x: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
    exit: (dir: number) => ({ opacity: 0, x: dir > 0 ? -40 : 40, transition: { duration: 0.25 } }),
  };

  return (
    <section data-ground className="relative section-rhythm overflow-hidden m-ground">
      <div className="relative z-10 m-container">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8 sm:mb-16"
        >
          <span
            className="block mb-4 font-mono text-[11px] uppercase tracking-widest"
            style={{ color: 'var(--m-ink-6)', letterSpacing: '0.22em' }}
          >
            Client Stories
          </span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <h2 className={`${SECTION_HEADING_CLASS} max-w-xl`} style={SECTION_HEADING_STYLE}>
              Results our clients actually talk about
            </h2>
            <p className="max-w-xs text-sm leading-relaxed" style={{ color: 'var(--m-ink-6)' }}>
              Real feedback from businesses we&apos;ve helped grow. Honest outcomes, no
              cherry-picked quotes.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* Main testimonial */}
          <div className="lg:col-span-2">
            <div className="relative overflow-hidden" style={{ minHeight: 320 }}>
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={active.id}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="p-8 lg:p-10"
                  style={{
                    background: 'var(--m-surface)',
                    border: '1px solid rgba(var(--m-ink-rgb),0.07)',
                  }}
                >
                  {/* Large opening quote mark */}
                  <div
                    className="text-7xl font-serif leading-none mb-4 select-none"
                    style={{
                      color: 'rgba(var(--m-ink-rgb),0.08)',
                      fontFamily: 'Georgia, serif',
                      lineHeight: 0.8,
                    }}
                  >
                    &quot;
                  </div>

                  <blockquote
                    className="text-lg leading-relaxed mb-8"
                    style={{ color: 'var(--m-ink-3)', lineHeight: 1.75, fontStyle: 'normal' }}
                  >
                    {active.quote}
                  </blockquote>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-11 h-11 flex items-center justify-center text-sm font-mono flex-shrink-0"
                        style={{ background: 'var(--m-ink)', color: 'var(--m-on-ink)' }}
                      >
                        {active.avatar}
                      </div>
                      <div>
                        <div
                          className="font-bold text-sm"
                          style={{
                            color: 'var(--m-ink)',
                            fontFamily: "'Archivo Narrow','Roboto Condensed',sans-serif",
                          }}
                        >
                          {active.name}
                        </div>
                        <div
                          className="text-xs font-mono mt-0.5"
                          style={{ color: 'var(--m-muted)' }}
                        >
                          {active.role} · {active.company}
                        </div>
                      </div>
                    </div>
                    <div
                      className="inline-flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase tracking-widest"
                      style={{
                        background: 'var(--m-ink)',
                        color: 'var(--m-on-ink)',
                        fontSize: 11,
                        letterSpacing: '0.1em',
                      }}
                    >
                      ↑ {active.result}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-4 mt-5">
              <button
                onClick={prev}
                className="w-10 h-10 flex items-center justify-center border transition-all"
                style={{ borderColor: 'rgba(var(--m-ink-rgb),0.12)', color: 'var(--m-ink-5)' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'var(--m-ink)';
                  (e.currentTarget as HTMLElement).style.borderColor = 'var(--m-ink)';
                  (e.currentTarget as HTMLElement).style.color = 'var(--m-on-ink)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                  (e.currentTarget as HTMLElement).style.borderColor =
                    'rgba(var(--m-ink-rgb),0.12)';
                  (e.currentTarget as HTMLElement).style.color = 'var(--m-ink-5)';
                }}
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={next}
                className="w-10 h-10 flex items-center justify-center border transition-all"
                style={{ borderColor: 'rgba(var(--m-ink-rgb),0.12)', color: 'var(--m-ink-5)' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'var(--m-ink)';
                  (e.currentTarget as HTMLElement).style.borderColor = 'var(--m-ink)';
                  (e.currentTarget as HTMLElement).style.color = 'var(--m-on-ink)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                  (e.currentTarget as HTMLElement).style.borderColor =
                    'rgba(var(--m-ink-rgb),0.12)';
                  (e.currentTarget as HTMLElement).style.color = 'var(--m-ink-5)';
                }}
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2 ml-2">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setDirection(i > activeIndex ? 1 : -1);
                      setActiveIndex(i);
                      startAutoPlay();
                    }}
                    className="transition-all"
                    style={{
                      width: i === activeIndex ? 24 : 6,
                      height: 2,
                      background:
                        i === activeIndex ? 'var(--m-ink)' : 'rgba(var(--m-ink-rgb),0.15)',
                    }}
                    aria-label={`Go to testimonial ${i + 1}`}
                  />
                ))}
              </div>
              <span className="ml-auto font-mono text-[11px]" style={{ color: 'var(--m-muted-4)' }}>
                {String(activeIndex + 1).padStart(2, '0')} /{' '}
                {String(testimonials.length).padStart(2, '0')}
              </span>
            </div>
          </div>

          {/* Side list */}
          <div className="hidden lg:flex flex-col gap-3">
            {testimonials.map((t, i) => (
              <button
                key={t.id}
                onClick={() => {
                  setDirection(i > activeIndex ? 1 : -1);
                  setActiveIndex(i);
                  startAutoPlay();
                }}
                className="text-left p-4 border transition-all duration-200"
                style={{
                  background: i === activeIndex ? 'var(--m-ink)' : 'var(--m-bg)',
                  borderColor: i === activeIndex ? 'var(--m-ink)' : 'rgba(var(--m-ink-rgb),0.07)',
                }}
                onMouseEnter={(e) => {
                  if (i !== activeIndex)
                    (e.currentTarget as HTMLElement).style.borderColor =
                      'rgba(var(--m-ink-rgb),0.2)';
                }}
                onMouseLeave={(e) => {
                  if (i !== activeIndex)
                    (e.currentTarget as HTMLElement).style.borderColor =
                      'rgba(var(--m-ink-rgb),0.07)';
                }}
              >
                <div className="flex items-center gap-3 mb-1">
                  <div
                    className="w-7 h-7 flex items-center justify-center text-[11px] font-mono flex-shrink-0"
                    style={{
                      background:
                        i === activeIndex
                          ? 'rgba(var(--m-on-ink-rgb),0.15)'
                          : 'rgba(var(--m-ink-rgb),0.05)',
                      color: i === activeIndex ? 'var(--m-on-ink)' : 'var(--m-ink-5)',
                    }}
                  >
                    {t.avatar}
                  </div>
                  <div>
                    <div
                      className="text-xs font-bold"
                      style={{
                        color: i === activeIndex ? 'var(--m-on-ink)' : 'var(--m-ink)',
                        fontFamily: "'Archivo Narrow','Roboto Condensed',sans-serif",
                      }}
                    >
                      {t.name}
                    </div>
                    <div
                      className="text-[11px] font-mono"
                      style={{
                        color:
                          i === activeIndex ? 'rgba(var(--m-on-ink-rgb),0.6)' : 'var(--m-muted-3)',
                      }}
                    >
                      {t.industry}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
